import { useState } from 'react';
import { Upload, CheckCircle, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';
import VerificacionCliente from '../components/VerificacionCliente';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CargarDocumentos() {
  const [expedienteVerificado, setExpedienteVerificado] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [exitoso, setExitoso] = useState(false);

  const handleVerificado = (expediente) => {
    setExpedienteVerificado(expediente);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar tamaño (max 15MB por archivo)
    const maxSize = 15 * 1024 * 1024;
    const archivosValidos = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} es demasiado grande (máx 15MB)`);
        return false;
      }
      return true;
    });
    
    setArchivos(prev => [...prev, ...archivosValidos]);
  };

  const eliminarArchivo = (index) => {
    setArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (archivos.length === 0) {
      toast.error('Por favor selecciona al menos un archivo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('expediente_id', expedienteVerificado.id);
      
      archivos.forEach((archivo, index) => {
        formData.append(`archivos`, archivo);
      });

      const response = await fetch(`${BACKEND_URL}/api/cargar-documentos-adicionales`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`✅ ${data.documentos_cargados} documento(s) cargado(s) exitosamente`);
        setExitoso(true);
      } else {
        const error = await response.json();
        toast.error(parseApiError(error.detail, 'Error al cargar documentos'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al cargar documentos');
    } finally {
      setUploading(false);
    }
  };

  // Pantalla de verificación
  if (!expedienteVerificado) {
    return (
      <VerificacionCliente
        onVerificado={handleVerificado}
        titulo="Cargar Documentos"
        descripcion="Sube los documentos solicitados por nuestro equipo"
      />
    );
  }

  // Pantalla de éxito
  if (exitoso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          </div>

          <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </div>

              <h1 className="text-3xl font-bold text-white mb-3">
                ✅ Documentos Recibidos
              </h1>
              
              <p className="text-gray-400 text-lg mb-6">
                Tus documentos han sido cargados exitosamente
              </p>

              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-5 mb-6">
                <p className="text-green-300 text-sm mb-2">
                  <strong>Expediente:</strong>
                </p>
                <p className="text-2xl font-bold text-green-400">
                  {expedienteVerificado.numero_expediente}
                </p>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 text-left">
                <p className="text-blue-300 text-sm mb-2">
                  <strong>Próximos pasos:</strong>
                </p>
                <ul className="text-gray-300 text-sm space-y-2 list-disc list-inside">
                  <li>Nuestro equipo revisará los documentos</li>
                  <li>Te contactaremos si necesitamos algo adicional</li>
                  <li>Recibirás actualizaciones por email</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Formulario de carga
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Cargar Documentos</h1>
          <p className="text-gray-400">Expediente: {expedienteVerificado.numero_expediente}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Selecciona tus Documentos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Zone */}
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center bg-gray-900/30">
                <Upload className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-300 mb-2">
                  Selecciona uno o más archivos
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  PDF, imágenes (JPG, PNG) - Máximo 15MB por archivo
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                    asChild
                  >
                    <span>
                      <FileText className="w-4 h-4 mr-2" />
                      Seleccionar Archivos
                    </span>
                  </Button>
                </label>
              </div>

              {/* Lista de archivos */}
              {archivos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-gray-300 font-semibold text-sm">
                    Archivos seleccionados ({archivos.length}):
                  </p>
                  {archivos.map((archivo, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-900/50 border border-gray-700 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-yellow-500" />
                        <div>
                          <p className="text-sm text-white">{archivo.name}</p>
                          <p className="text-xs text-gray-500">
                            {(archivo.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => eliminarArchivo(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Botón enviar */}
              <Button
                type="submit"
                disabled={uploading || archivos.length === 0}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cargando {archivos.length} archivo(s)...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Cargar {archivos.length} Documento(s)
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
